import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useRunSupportMail,
  useSaveSupportMailSettings,
  useSupportMailSettings
} from '../../api/queries'
import type { SupportMailSettingsInput, SupportType } from '../../api/types'
import { Card, ErrorBox, Loading } from '../../components/ui'

/**
 * Mail ind i supportindbakken (docs/DECISIONS.md beslutning 073).
 *
 * <b>Den står som standard til fra, og det er ikke tilfældigt.</b> Læsningen
 * kræver rettigheden <code>Mail.Read</code>, som app-only rækker over <b>hver
 * eneste postkasse i tenanten</b>, indtil den er begrænset med en application
 * access policy. Det står på siden, så ingen slår den til uden at vide det.
 */
export function SupportMailPage() {
  const { data, isLoading, error } = useSupportMailSettings()
  const save = useSaveSupportMailSettings()
  const run = useRunSupportMail()

  const [form, setForm] = useState<SupportMailSettingsInput | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (data && form === null) {
      setForm({
        enabled: data.enabled,
        mailbox: data.mailbox ?? '',
        folder: data.folder ?? '',
        intervalMinutes: data.intervalMinutes,
        defaultType: data.defaultType
      })
    }
  }, [data, form])

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data || !form) return null

  function set<K extends keyof SupportMailSettingsInput>(key: K, value: SupportMailSettingsInput[K]) {
    setForm((current) => (current === null ? current : { ...current, [key]: value }))
  }

  async function submit() {
    if (!form) return

    setProblem(null)
    setNotice(null)

    try {
      await save.mutateAsync(form)
      setNotice('Opsætningen er gemt.')
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Opsætningen kunne ikke gemmes.')
    }
  }

  async function fetchNow() {
    setProblem(null)
    setNotice(null)

    try {
      const result = await run.mutateAsync()

      if (result.error) {
        setProblem(result.error)
        return
      }

      setNotice(
        `Hentet: ${result.created} nye henvendelser, ${result.appended} svar på eksisterende, ` +
          `${result.skipped} sprunget over (læst ind før).`
      )
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Postkassen kunne ikke læses.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Mail ind i indbakken
        </h1>
        <Link className="btn btn--sm" to="/support">
          Til henvendelserne
        </Link>
      </div>

      {!data.microsoftReady && (
        <div className="error-box">
          Microsoft 365 er ikke sat op. Udfyld tenant, klient-id og hemmelighed under{' '}
          <Link to="/admin/microsoft365">Microsoft 365</Link>, før postkassen kan læses.
        </div>
      )}

      <Card title="Sikkerhed - læs det her først">
        <p className="small" style={{ marginTop: 0 }}>
          Læsningen bruger rettigheden <code>Mail.Read</code> som applikationsrettighed. Den giver
          appen adgang til <strong>hver eneste postkasse i tenanten</strong> - ikke kun
          supportpostkassen.
        </p>
        <p className="small">
          Den skal derfor begrænses med en <strong>application access policy</strong> i Exchange
          Online, som binder appen til supportpostkassen. Det er den samme begrænsning, som{' '}
          <code>Mail.Send</code> skal have (se docs/INSTALL.md afsnit 1h). Uden den kan en
          kompromitteret klienthemmelighed læse direktørens mail.
        </p>
        <p className="small muted">
          Derfor står læsningen som standard til fra, og derfor kan opsætningen prøves af med
          &quot;Hent nu&quot;, uden at den slås til.
        </p>
      </Card>

      <Card title="Postkassen">
        <div className="form-grid">
          <div>
            <div className="tiny muted">Postkasse</div>
            <input
              type="email"
              value={form.mailbox ?? ''}
              onChange={(event) => set('mailbox', event.target.value)}
              placeholder="support@tc-el.dk"
              maxLength={200}
            />
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Skal være en rigtig postkasse - et alias kan ikke læses.
            </div>
          </div>

          <div>
            <div className="tiny muted">Mappe</div>
            <input
              value={form.folder ?? ''}
              onChange={(event) => set('folder', event.target.value)}
              placeholder="Indbakken"
              maxLength={100}
            />
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Tom = Indbakken.
            </div>
          </div>

          <div>
            <div className="tiny muted">Kig efter ny mail hvert</div>
            <select
              value={form.intervalMinutes}
              onChange={(event) => set('intervalMinutes', Number(event.target.value))}
            >
              <option value={1}>minut</option>
              <option value={5}>5. minut</option>
              <option value={15}>kvarter</option>
              <option value={30}>halve time</option>
              <option value={60}>time</option>
            </select>
          </div>

          <div>
            <div className="tiny muted">En mail bliver til</div>
            <select
              value={form.defaultType}
              onChange={(event) => set('defaultType', event.target.value as SupportType)}
            >
              <option value="customer">Kundehenvendelse</option>
              <option value="system">Problem med systemet</option>
            </select>
            <div className="tiny muted" style={{ marginTop: 4 }}>
              Postkassen kender ikke forskellen, så den skal vælges. Slagsen kan rettes bagefter.
            </div>
          </div>
        </div>

        <label className="row" style={{ gap: 8, alignItems: 'center', marginTop: 14 }}>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => set('enabled', event.target.checked)}
          />
          <span className="small">
            <strong>Læs postkassen automatisk</strong>
          </span>
        </label>
      </Card>

      <Card title="Sådan virker det">
        <ul className="small" style={{ marginTop: 0, paddingLeft: 18 }}>
          <li>
            <strong>Kun ulæste mails hentes</strong>, og en mail markeres først som læst, når den
            står i systemet. Går strømmen midt i en runde, kommer resten igen.
          </li>
          <li>
            <strong>Et svar lander på den rigtige henvendelse.</strong> Systemet skriver{' '}
            <code>[TC-42]</code> i emnet på det, det sender ud, og genkender det i svaret. Er emnet
            skrevet om, bruges mailtrådens eget id.
          </li>
          <li>
            <strong>Teksten bliver altid til ren tekst.</strong> En mail udefra er skrevet af en
            fremmed - blev den vist som HTML, kunne den køre noget i en kollegas browser.
          </li>
          <li>
            <strong>Vedhæftede filer hentes ikke</strong> ind i systemet, men det står på beskeden,
            at der var nogle, så ingen tror, at et foto er forsvundet. De ligger i postkassen.
          </li>
          <li>
            Er afsenderens adresse kendt på en kunde, foreslås kunden. Det er et forslag - en
            afsenderadresse kan forfalskes, og koblingen giver ingen adgang.
          </li>
        </ul>
      </Card>

      {problem && <div className="error-box">{problem}</div>}
      {notice && <div className="map-notice">{notice}</div>}

      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn--primary" disabled={save.isPending} onClick={() => void submit()}>
          {save.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
        <button
          className="btn"
          disabled={run.isPending || !data.isConfigured}
          onClick={() => void fetchNow()}
        >
          {run.isPending ? 'Henter ...' : 'Hent nu'}
        </button>
        <span className="tiny muted" style={{ alignSelf: 'center' }}>
          &quot;Hent nu&quot; kører også, når læsningen står til fra - så opsætningen kan prøves af
          først.
        </span>
      </div>
    </div>
  )
}
